import React, { useRef, useMemo } from "react";

let _counter = 0;

/**
 * Animated sparkles icon — one big sparkle plus four smaller satellites,
 * laid out randomly per instance with a guaranteed no-overlap check.
 *
 * Layout is seeded from the component's React id (or a caller-supplied
 * `seed`) so it is stable across re-renders and SSR hydration.
 *
 * @param {object} props
 * @param {string} [props.color]       - Any CSS colour. Defaults to
 *                                       `currentColor` so it inherits from
 *                                       surrounding text.
 * @param {number|string} [props.size] - Width/height (e.g. 24, "1.25em").
 * @param {number} [props.duration]    - Loop duration in seconds. Default 2.1.
 * @param {string} [props.title]       - Accessible label. Default "Loading".
 * @param {string|number} [props.seed] - Optional seed to force a specific
 *                                       layout. Same seed = same positions.
 */
export default function Sparkles({
  color = "currentColor",
  size = 24,
  duration = 2.1,
  title = "Loading",
  seed,
  ...rest
}) {
  const uidRef = useRef(null);
  if (uidRef.current === null) uidRef.current = `sparkles-${++_counter}`;
  const uid = uidRef.current;
  const shapeId = `sparkle-shape-${uid}`;
  const d = `${duration}s`;

  const layout = useMemo(
    () => placeSparkles(seed ?? uid, duration),
    [seed, uid, duration],
  );

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      style={{ color }}
      fill="currentColor"
      role="img"
      aria-label={title}
      {...rest}
    >
      <title>{title}</title>
      <defs>
        <path
          id={shapeId}
          d="M0,-10 C1,-4 4,-1 10,0 C4,1 1,4 0,10 C-1,4 -4,1 -10,0 C-4,-1 -1,-4 0,-10 Z"
        />
      </defs>

      {layout.map((s, i) => (
        <g key={i} transform={`translate(${s.x} ${s.y})`}>
          <g opacity="0">
            <animate
              attributeName="opacity"
              values={s.big ? "0;1;1;0" : "0;1;0"}
              keyTimes={s.big ? "0;0.2;0.55;0.9" : undefined}
              dur={d}
              begin={s.begin}
              repeatCount="indefinite"
            />
            <animateTransform
              attributeName="transform"
              type="scale"
              values={
                s.big
                  ? `0;${s.scale};${(s.scale * 0.92).toFixed(3)};0`
                  : `0;${s.scale};0`
              }
              keyTimes={s.big ? "0;0.25;0.55;0.9" : undefined}
              dur={d}
              begin={s.begin}
              repeatCount="indefinite"
            />
            <use href={`#${shapeId}`} />
          </g>
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Layout generator                                                   */
/* ------------------------------------------------------------------ */

// The sparkle path has radius 10 in its own coordinate space. Two sparkles
// whose centres are closer than (scaleA + scaleB) * COLLISION_R have
// overlapping cores. We use 7 rather than the full 10 because the star
// shape is narrow along the diagonals — tips can just barely kiss without
// the bright cores overlapping. Bump towards 10 for stricter separation.
const COLLISION_R = 7;
const VIEWBOX = 24;

function placeSparkles(seedInput, duration) {
  const rand = mulberry32(hashString(String(seedInput)));

  const placed = [];

  // Big anchor sparkle — kept roughly central so the icon stays balanced.
  const big = {
    x: 9.5 + rand() * 5, // [9.5, 14.5]
    y: 9.5 + rand() * 5, // [9.5, 14.5]
    scale: 0.7 + rand() * 0.08, // [0.70, 0.78]
    begin: "0s",
    big: true,
  };
  placed.push(big);

  // Four satellites — random size, random position, rejection-sampled so
  // nothing overlaps anything already placed.
  for (let i = 0; i < 4; i++) {
    const scale = 0.28 + rand() * 0.2; // [0.28, 0.48]
    const margin = scale * 10; // keep the full star inside the tile
    const lo = margin;
    const hi = VIEWBOX - margin;

    let pick = null;
    for (let attempt = 0; attempt < 400; attempt++) {
      const x = lo + rand() * (hi - lo);
      const y = lo + rand() * (hi - lo);
      if (!collides(x, y, scale, placed)) {
        pick = { x, y, scale };
        break;
      }
    }

    // Pathological fallback: shrink and try again on a wider grid.
    if (!pick) {
      const s2 = 0.26;
      const m2 = s2 * 10;
      for (let attempt = 0; attempt < 400; attempt++) {
        const x = m2 + rand() * (VIEWBOX - 2 * m2);
        const y = m2 + rand() * (VIEWBOX - 2 * m2);
        if (!collides(x, y, s2, placed)) {
          pick = { x, y, scale: s2 };
          break;
        }
      }
    }
    // If even that fails, skip this satellite rather than draw an overlap.
    if (!pick) continue;

    // Begin time: a random fraction of the loop, biased so the satellites
    // are spread across the cycle rather than all firing at once.
    const slot = (i + 0.25 + rand() * 0.5) / 5; // ~0.25 .. ~1.0 of loop
    pick.begin = `${(duration * slot).toFixed(2)}s`;
    pick.big = false;

    placed.push(pick);
  }

  return placed;
}

function collides(x, y, scale, placed) {
  for (const p of placed) {
    const dx = x - p.x;
    const dy = y - p.y;
    const minDist = (scale + p.scale) * COLLISION_R;
    if (dx * dx + dy * dy < minDist * minDist) return true;
  }
  return false;
}

/* FNV-1a → uint32 hash, then mulberry32 for a fast seeded PRNG. */
function hashString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
